import { CommonModule } from '@angular/common';
import { InjectionToken, NgModule } from '@angular/core';

import { GestureModule } from '@frontend/sports/gesture/feature';
import { ActiveModal } from '@frontend/sports/modal/feature';
import { SlideComponent, SlideshowComponent } from '@frontend/sports/slideshow/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { EffectsModule } from '@ngrx/effects';
import { StoreConfig, StoreModule } from '@ngrx/store';
import { RxLet } from '@rx-angular/template/let';

import { ArcModule } from '../../lib/arc/arc.module';
import { ShowInEpcotDirective } from '../../lib/common/epcot-config.directive';
import { EpcotNotificationComponent } from '../../lib/common/epcotNotifications/notification.component';
import BetslipBaseModule from '../base/betslip-base.module';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
} from '../base/services/betslip-messages-provider.service';
import { betslipBaseStateSelector } from '../base/store/selectors';
import { betslipFeatureKey } from '../base/store/state';
import { DigitalAccaBoostModule } from '../modules/acca-boost/digital-acca-boost.module';
import { SharedAccaBoostModule } from '../modules/acca-boost/shared-acca-boost.module';
import BetslipAffiliateModule from '../modules/affiliates/affiliate.module';
import BetslipAlertsModule from '../modules/alerts/alerts.module';
import { DigitalBalanceHeaderModule } from '../modules/balance-header/digital-balance-header.module';
import { BetBuilderPopupComponent } from '../modules/bet-builder-popup/components/bet-builder-popup.component';
import { BetBuilderModule } from '../modules/bet-builder/bet-builder.module';
import BetslipDigitalBetGenerationModule from '../modules/bet-generation/betslip-digital-bet-generation.module';
import BetslipDigitalBetPlacementModule from '../modules/betplacement/betslip-digital-betplacement.module';
import { BetslipBarModule } from '../modules/betslip-bar/betslip-bar-digital.module';
import { BetslipBarComponent } from '../modules/betslip-bar/components/betslip-bar.component';
import BetslipDigitalComboBetModule from '../modules/combo-bet/betslip-digital-combo-bet.module';
import { BetslipDigitalCommonModule } from '../modules/common/digital-module';
import { EditBetAddPicksContainerMobileComponent } from '../modules/edit-bet/components/add-picks/edit-bet-add-picks-container-mobile.component';
import { EditMybetContainerComponent } from '../modules/edit-bet/components/edit-bet-container.component';
import EditBetModule from '../modules/edit-bet/edit-bet.module';
import { HiddenMarketModule } from '../modules/hidden-market/hidden-market.module';
import { LineSwitcherModule } from '../modules/line-switcher/line-switcher.module';
import { LinearBetBuilderModule } from '../modules/linear-bet-builder/linear-bet-builder.module';
import { LinearBetslipModule } from '../modules/linear/linear-betslip-module';
import { BetslipDigitalMultiReturnsMessageModule } from '../modules/multi-returns-message/betslip-digital-multi-returns-message.module';
import { BetslipOddsPreferencePopupModule } from '../modules/odds-preference-popup/odds-preference-popup.module';
import OnboardingModule from '../modules/onboarding/onboarding.module';
import { BetslipOverAskDigitalModule } from '../modules/over-ask/over-ask-digital.module';
import { BetslipPanicsliderComponent } from '../modules/panic-slider/betslip-panicslider.component';
import BetslipDigitalPicksModule from '../modules/picks/betslip-digital-picks.module';
import { betslipPickIdsSelector, betslipPicksListCountSelectorIncludeBetbuilderLegs, betslipPicksListSelector } from '../modules/picks/selectors';
import { BetslipTrackingSelectionService } from '../modules/picks/services/betslip-tracking.service';
import { BetslipDigitalQuickBetModule } from '../modules/quick-bet/betslip-digital-quick-bet.module';
import { QuickDepositModule } from '../modules/quick-deposit/quick-deposit.module';
import BetslipDigitalQuickLinksModule from '../modules/quick-links/betslip-digital-quick-links.module';
import BetslipDigitalResultModule from '../modules/result/betslip-digital-result.module';
import { RemindMeLaterNotificationComponent } from '../modules/reward-tokens/components/digital/remind-me-later-notification.component';
import { RewardTokensBannerComponent } from '../modules/reward-tokens/components/digital/reward-tokens-banner.component';
import { DigitalRewardTokensModule } from '../modules/reward-tokens/digital-reward-tokens.module';
import { RewardTokensBootstrapService } from '../modules/reward-tokens/services/reward-tokens-bootstrap.service';
import DigitalSettingsModule from '../modules/settings/digital-settings.module';
import BetslipSettingsModule from '../modules/settings/settings.module';
import BetslipDigitalSingleBetModule from '../modules/single-bet/betslip-digital-single-bet.module';
import BetslipDigitalStakeModule from '../modules/stake/betslip-digital-stake.module';
import BetslipStorageModule from '../modules/storage/betslip-storage.module';
import BetslipDigitalSummaryModule from '../modules/summary/betslip-digital-summary.module';
import BetslipDigitalSystemBetModule from '../modules/system-bet/betslip-digital-system-bet.module';
import { BetslipDigitalTeaserModule } from '../modules/teaser-bet/digital-module';
import BetslipDigitalTrackingModule from '../modules/tracking/digital-tracking.module';
import { betslipCurrentTypeSelector } from '../modules/types/base/selectors';
import BetslipDigitalTypesModule from '../modules/types/betslip-digital-types.module';
import {
    betslipTypeStateSelector,
    editBetAddedPicksListSelector,
    editBetPickStateSelector,
    editBetPicksListSelector,
} from '../modules/types/selectors';
import { BetslipDigitalUpsellModule } from '../modules/upsell/betslip-digital-upsell.module';
import BetslipDigitalValidationModule from '../modules/validation/betslip-digital-validation.module';
import WalletTypeContainerModule from '../modules/wallet-type/wallet-type-container.module';
import BetslipWebBridgeModule from '../modules/web-bridge/betslip-web-bridge.module';
import { DepositDetailsComponent } from './components/betslip-deposit-details.component';
import { BetslipDisplayModeToggleComponent } from './components/betslip-display-mode-toggle/betslip-display-mode-toggle.component';
import { BetslipEditStateComponent } from './components/betslip-edit-state.component';
import { BetslipEmptyStateComponent } from './components/betslip-empty-state.component';
import { BetslipLinearEditStateComponent } from './components/betslip-linear-edit-state.component';
import { BetslipLinearTypeListComponent } from './components/betslip-linear-type-list/betslip-linear-type-list.component';
import { BetslipResultStateComponent } from './components/betslip-result-state.component';
import { BetslipReturnsMessagesComponent } from './components/betslip-returns-messages.component';
import { BetslipComponent } from './components/betslip/betslip.component';
import { QuickBetComponent } from './components/quick-bet.component';
import Effects from './effects';
import { digitalRootReducer } from './reducer';
import BetslipMessageProviderService from './services/betslip-message-provider.service';
import { BetslipReturnsMessagesAccaBoostService } from './services/betslip-returns-messages-acca-boost.service';
import { BetslipReturnsMessagesNotificationService } from './services/betslip-returns-messages-notification.service';
import { BetslipReturnsMessagesVisibilityService } from './services/betslip-returns-messages-visbility.service';
import LinearBetslipMessageProviderService from './services/linear-betslip-message-provider.service';
import { MultiReturnsMessageNotificationService } from './services/multi-returns-message-notification.service';
import { RemindMeLaterService } from './services/remind-me-later.service';

const BETSLIP_STORE_CONFIG_TOKEN = new InjectionToken<StoreConfig<any>>('Betslip Store Config');

@NgModule({
    imports: [
        ArcModule,
        CommonModule,
        RxLet,
        StoreModule.forFeature(betslipFeatureKey, digitalRootReducer, BETSLIP_STORE_CONFIG_TOKEN),
        EffectsModule.forFeature([Effects]),
        BetslipDigitalPicksModule,
        BetslipDigitalTypesModule,
        BetslipDigitalSingleBetModule,
        BetslipDigitalComboBetModule,
        BetslipDigitalSystemBetModule,
        BetslipDigitalTeaserModule,
        BetslipDigitalStakeModule,
        BetslipDigitalBetPlacementModule,
        BetslipDigitalBetGenerationModule,
        BetslipDigitalValidationModule,
        BetslipDigitalSummaryModule,
        BetslipDigitalQuickLinksModule,
        BetslipDigitalResultModule,
        DigitalSettingsModule,
        BetslipAlertsModule,
        BetslipStorageModule.forParent(BETSLIP_STORE_CONFIG_TOKEN),
        WalletTypeContainerModule,
        BetslipDigitalMultiReturnsMessageModule,
        DigitalRewardTokensModule,
        BetslipOverAskDigitalModule,
        LineSwitcherModule,
        SharedAccaBoostModule,
        GestureModule,
        DigitalAccaBoostModule,
        BetslipDigitalUpsellModule,
        OnboardingModule,
        BetslipAffiliateModule,
        BetslipDigitalQuickBetModule,
        EditBetModule,
        BetslipDigitalCommonModule,
        BetslipDigitalTrackingModule,
        DigitalBalanceHeaderModule,
        QuickDepositModule,
        BetslipOddsPreferencePopupModule,
        BetslipBarModule,
        HiddenMarketModule,
        BetslipBaseModule,
        BetslipWebBridgeModule,
        BetBuilderModule,
        EpcotNotificationComponent,
        ShowInEpcotDirective,
        LinearBetslipModule,
        RemindMeLaterNotificationComponent,
        LinearBetBuilderModule,
        BetBuilderPopupComponent,
        SlideshowComponent,
        SlideComponent,
        IconCustomComponent,
    ],
    declarations: [
        BetslipComponent,
        BetslipEditStateComponent,
        BetslipEmptyStateComponent,
        BetslipResultStateComponent,
        BetslipReturnsMessagesComponent,
        QuickBetComponent,
        BetslipPanicsliderComponent,
        DepositDetailsComponent,
        BetslipLinearEditStateComponent,
        BetslipDisplayModeToggleComponent,
        BetslipLinearTypeListComponent,
    ],
    providers: [
        { provide: BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, useClass: BetslipMessageProviderService },
        { provide: LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, useClass: LinearBetslipMessageProviderService },
        BetslipReturnsMessagesNotificationService,
        MultiReturnsMessageNotificationService,
        BetslipReturnsMessagesVisibilityService,
        BetslipReturnsMessagesAccaBoostService,
        RemindMeLaterService,
        ActiveModal,
        BetslipTrackingSelectionService,
    ],

    exports: [BetslipComponent],
})
export class BetslipDigitalModule {
    readonly BetslipPickActions = BetslipDigitalPicksModule.BetslipPickActions;
    readonly EditBetActions = EditBetModule.EditBetActions;
    readonly SettingsActions = BetslipSettingsModule.SettingsActions;
    readonly RewardTokensActions = DigitalRewardTokensModule.RewardTokensActions;

    readonly betslipComponent = BetslipComponent;
    readonly editBetComponent = EditMybetContainerComponent;
    readonly quickBetContainerComponent = QuickBetComponent;
    readonly editBetAddPicksContainerMobileComponent = EditBetAddPicksContainerMobileComponent;
    readonly rewardTokensBannerComponent = RewardTokensBannerComponent;
    readonly betslipReturnsMessagesComponent = BetslipReturnsMessagesComponent;
    readonly betslipBarComponent = BetslipBarComponent;
    readonly betBuilderPopUpComponent = BetBuilderPopupComponent;

    readonly exported = {
        selectors: {
            betslipTypeCurrentTypeSelector: betslipCurrentTypeSelector,
            betslipTypeStateSelector,
            betslipPickIdsSelector,
            betslipPicksListSelector,
            betslipBaseStateSelector,
            editBetPicksListSelector,
            editBetAddedPicksListSelector,
            editBetPickStateSelector,
            betslipPicksListCountSelectorIncludeBetbuilderLegs,
        },
    };

    constructor(rewardTokensBootstrap: RewardTokensBootstrapService) {
        rewardTokensBootstrap.init();
    }
}
